/**
 * SigningPlanPanel — ordem de assinatura com que o contrato será enviado (AF-4138).
 *
 * O plano vem do contract (validate-for-signature), com a mesma regra do envio: a tela
 * mostra o que o provedor vai receber. Todas as ordens configuradas aparecem, sem
 * renumerar; uma ordem opcional sem signatário fica esmaecida.
 */
import type { SigningStep } from './signingPlan';

const SIDE_LABELS: Record<string, string> = {
    ASSIGNOR: 'Cliente',
    RECIPIENT: 'Credora',
    ANY: 'Qualquer lado',
};

interface SigningPlanPanelProps {
    plan: SigningStep[] | null;
    loading: boolean;
}

export default function SigningPlanPanel({ plan, loading }: SigningPlanPanelProps) {
    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ordem de assinatura</h3>
                {loading && <span className="text-xs text-gray-500 dark:text-gray-400">atualizando...</span>}
            </div>
            {!plan && (
                <p className="p-4 text-sm text-gray-500 dark:text-gray-400 italic">
                    A ordem aparece quando o tipo de contrato tem estrutura configurada e a seleção é válida.
                </p>
            )}
            {plan && (
                <ol className="p-4 space-y-3">
                    {plan.map(step => (
                        <li key={step.order} className="flex gap-3">
                            <span className="w-6 h-6 shrink-0 rounded-full bg-gray-200 dark:bg-slate-700 text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center">
                                {step.order}
                            </span>
                            <div className="flex-1 space-y-1">
                                {step.roles.map(role => {
                                    const empty = role.signers.length === 0;
                                    return (
                                        <div
                                            key={`${role.role}-${role.participantSource}`}
                                            className={`text-sm ${empty ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}
                                        >
                                            <span className="font-medium">{role.label || role.role}</span>
                                            <span className="text-xs ml-1">({SIDE_LABELS[role.participantSource] || role.participantSource})</span>
                                            {': '}
                                            {empty
                                                ? <span className="italic">sem signatário{role.required ? '' : ' (opcional)'}</span>
                                                : role.signers.map(s => s.name).join(', ')}
                                        </div>
                                    );
                                })}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
